import {
  AvroOptional,
  log as signalsLogRenderError,
} from "@indeed/signals-schema/com/indeed/signals/schema/reactnative/RNRenderErrorV5";
import React, { type ErrorInfo, type ReactNode } from "react";

import { EmptyState } from "../../mads";
import { useI18n } from "../I18n";
import { getRnErrorProviderId } from "../util/getRnErrorProviderId";
import { getDeviceProperties } from "./LoggableDeviceProperties";

class UnknownError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "UnknownError";
  }
}

/**
 * Thrown exceptions can be of any type. In case a string or otherwise
 * is being thrown we wrap a generic error around it so that we always
 * have something of Error type.
 *
 * @param error pretty much anything
 * @returns an instance of Error
 */
// eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents -- FIXME
export function sanitizeError(error: Error | string | unknown): Error {
  if (error instanceof Error) {
    return error;
  }

  if (typeof error === "string") {
    return new UnknownError(error);
  }

  return new UnknownError("Unknown error");
}

/**
 * Log an error that occurred during rendering using signals
 * @param error the error that occurred during rendering
 * @param componentStack information about the rendering tree provided by React
 */
function logRenderError(provider: string, error: Error, componentStack: string): void {
  const { message, name, stack = "" } = error;

  signalsLogRenderError({
    provider: AvroOptional.string(provider),
    deviceProperties: getDeviceProperties(),
    message,
    name,
    stack,
    componentStack,
    providerId: { ProviderId: getRnErrorProviderId(provider) },
  });
}

export interface RenderErrorBoundaryProps {
  children: ReactNode;

  /**
   * The name of the provider that emits the log. It will go into the `provider` field of the log.
   */
  provider: string;

  onError?: (error: Error | string | null, info: ErrorInfo) => void;
}

/**
 * Function component that renders the error fallback using MADS EmptyState.
 * Separated from the class component because useI18n is a hook and cannot be
 * called inside a class render method.
 */
function RenderErrorState() {
  const { gettext } = useI18n();
  return (
    <EmptyState
      title={gettext("Something went wrong on our end")}
      description={gettext("We’re working to fix it. Please try again later.")}
    />
  );
}

/**
 * RenderErrorBoundary is a wrapper component that catches errors in its children
 * and logs them using signals
 *
 * See https://react.dev/reference/react/Component#catching-rendering-errors-with-an-error-boundary
 */
export class RenderErrorBoundary extends React.Component<
  RenderErrorBoundaryProps,
  { hasError: boolean }
> {
  constructor(props: RenderErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  /**
   * Will be called if an error has been thrown in a child component.
   * We use it here to record the state that an error has occurred.
   *
   * https://react.dev/reference/react/Component#static-getderivedstatefromerror
   */
  static getDerivedStateFromError() {
    return { hasError: true };
  }

  /**
   * Will be called if an error has been thrown in a child component.
   * We use it here to log the error using signals
   *
   * https://react.dev/reference/react/Component#componentdidcatch
   *
   * @param error typically an Error object but could be a string or anything really
   * @param info additional context from the rendering engine about what happened
   */
  componentDidCatch(error: Error | string | null, info: ErrorInfo) {
    const componentStack = info.componentStack ?? "<Component stack not available>";
    this.props.onError?.(error, info);
    logRenderError(this.props.provider, sanitizeError(error), componentStack);
  }

  render() {
    if (this.state.hasError) {
      return <RenderErrorState />;
    }

    return this.props.children;
  }
}
